import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface Suggestion {
  name: string;
  confidence: number;
  category?: string;
}

interface AISuggestionButtonProps {
  question: string;
  category?: string;
  existingTags?: string[];
  type: 'subcategories' | 'tags' | 'both';
  onSelect: (suggestion: string) => void;
  disabled?: boolean;
  size?: 'sm' | 'default';
}

export const AISuggestionButton = ({ 
  question, 
  category, 
  existingTags = [], 
  type, 
  onSelect, 
  disabled = false,
  size = 'sm'
}: AISuggestionButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<{
    subcategories: Suggestion[];
    tags: Suggestion[];
    reasoning: string;
  } | null>(null);
  const [open, setOpen] = useState(false);

  const fetchSuggestions = async () => {
    if (!question || question.trim().length < 10) {
      toast({
        title: "Question too short",
        description: "Please enter at least 10 characters in the question field",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('ai-bulk-suggestions', {
        body: {
          question: question.trim(),
          category,
          existingTags,
          requestType: type
        }
      });

      if (error) throw error;

      setSuggestions(data);
      setOpen(true);
    } catch (error) {
      console.error('Error fetching AI suggestions:', error);
      toast({
        title: "Error",
        description: "Failed to fetch AI suggestions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.9) return "bg-green-100 text-green-800 border-green-200";
    if (confidence >= 0.7) return "bg-blue-100 text-blue-800 border-blue-200";
    if (confidence >= 0.5) return "bg-yellow-100 text-yellow-800 border-yellow-200";
    return "bg-gray-100 text-gray-800 border-gray-200";
  };

  const getRelevantSuggestions = () => {
    if (!suggestions) return [];
    
    if (type === 'subcategories') return suggestions.subcategories;
    if (type === 'tags') return suggestions.tags;
    return [...suggestions.subcategories, ...suggestions.tags];
  };

  const relevantSuggestions = getRelevantSuggestions();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size={size}
          onClick={fetchSuggestions}
          disabled={disabled || isLoading || !question?.trim()}
          className="h-8 w-8 p-0 border-dashed hover:border-solid"
          title="Get AI suggestions"
        >
          {isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Sparkles className="h-3 w-3" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-blue-600" />
            <h4 className="font-medium text-sm">
              AI Suggestions {type === 'both' ? '' : `for ${type}`}
            </h4>
          </div>
          
          {suggestions && (
            <ScrollArea className="max-h-60">
              {relevantSuggestions.length > 0 ? (
                <div className="space-y-2">
                  {type === 'both' && suggestions.subcategories.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Sub-categories:</p>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {suggestions.subcategories.map((item, index) => (
                          <Badge
                            key={index}
                            variant="outline"
                            className={`cursor-pointer text-xs ${getConfidenceColor(item.confidence)}`}
                            onClick={() => {
                              onSelect(item.name);
                              setOpen(false);
                            }}
                          >
                            {item.name} ({Math.round(item.confidence * 100)}%)
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {type === 'both' && suggestions.tags.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Tags:</p>
                      <div className="flex flex-wrap gap-1 mb-2">
                        {suggestions.tags.map((item, index) => (
                          <Badge
                            key={index}
                            variant="outline"
                            className={`cursor-pointer text-xs ${getConfidenceColor(item.confidence)}`}
                            onClick={() => {
                              onSelect(item.name);
                              setOpen(false);
                            }}
                          >
                            {item.name} ({Math.round(item.confidence * 100)}%)
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {type !== 'both' && relevantSuggestions.map((item, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className={`cursor-pointer text-xs mr-1 mb-1 ${getConfidenceColor(item.confidence)}`}
                      onClick={() => {
                        onSelect(item.name);
                        setOpen(false);
                      }}
                    >
                      {item.name} ({Math.round(item.confidence * 100)}%)
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No suggestions available</p>
              )}
              
              {suggestions.reasoning && (
                <div className="mt-3 pt-2 border-t">
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">AI reasoning:</span> {suggestions.reasoning}
                  </p>
                </div>
              )}
            </ScrollArea>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};